import { values } from 'lodash';
import { useCallback, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { BillingService, BuildingOrg, Contract, ContractId, OrgId } from 'shared/models';
import { sortNamesBy } from 'logic/sortNames';
import { BuildingOrgListItem } from '../buildingOrgListItem/BuildingOrgListItem';

require('./BuildingOrgList.css');

export type Props = {
    buildingOrgs: Record<OrgId, BuildingOrg>;
    contracts: Record<ContractId, Contract>;
    loading: boolean;
    unbilledServices: readonly BillingService[];
    overbilledServices: readonly BillingService[];
    canEdit: boolean;
    onEdit(orgId: OrgId): void;
};

export const BuildingOrgList = ({ buildingOrgs, contracts, loading, unbilledServices, overbilledServices, canEdit, onEdit }: Props) => {
    const { t } = useTranslation();

    const order =
        values(buildingOrgs)
            .sort(sortNamesBy(bo => bo.displayName))
            .map(o => o.orgId);
    
    const handleClickEdit = useCallback((e: MouseEvent<HTMLElement>) => {
        const { dataset } = e.currentTarget;
        onEdit(dataset.id!);
    }, [ onEdit ]);

    return (
        <div className="building-org-list">
            <table className="ui single line compact table">
                <colgroup>
                    <col className="_org" />
                    <col className="_billing" />
                    <col className="_action" />
                </colgroup>
                <thead>
                    <tr>
                        <th>{ t('ORGS.COLUMN.NAME') }</th>
                        <th>{ t('ORGS.COLUMN.BILLING') }</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    { loading && order.length === 0 &&
                        <tr>
                            <td colSpan={ 3 }>
                                <i className="spinner loading icon"/>
                                <span>
                                    { t('LOADING') }
                                </span>
                            </td>
                        </tr>
                    }
                    { order.map(id => buildingOrgs[id]).map(org =>
                        <BuildingOrgListItem
                            key={ org.orgId }
                            org={ org }
                            contract={ org.contractId ? contracts[org.contractId] : undefined }
                            onClickEdit={ canEdit ? handleClickEdit : undefined }
                            unbilledServices={ unbilledServices }
                            overbilledServices={ overbilledServices }
                        />
                    ) }
                </tbody>
            </table>
        </div>
    );
};
